import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Beef, CalendarDays, ChevronRight } from 'lucide-react'
import MediaThumb from '../MediaThumb'
import { getKraalFeed } from '../../services/api'
import { EXIT_LABEL, feedHeadline, fmtN, formatDay, pctText } from '../../utils/presence'

// The last dip day at a glance (plan smartkraal-owner-experience B2): the
// headline, up to eight faces and the movements of the last 28 days, linking to
// the feed. Reads the same payload as the feed page with a short strip. Renders
// nothing on a ranch without a kraal or before its first dip day. Scoped
// server-side to the owner's own animals when their account is linked.
const STRIP = 8

export default function KraalFeedCard({ ranchId, className = '' }) {
  const { data } = useQuery({
    queryKey: ['kraalFeed', ranchId, { days: 28, limit: STRIP }],
    queryFn: ({ signal }) => getKraalFeed(ranchId, { days: 28, limit: STRIP }, { signal }),
    enabled: !!ranchId,
    staleTime: 5 * 60 * 1000,
  })
  if (!data?.configured || !data.last_dip_day) return null
  const dip = (data.kraal_days || []).find((d) => d.is_dip_day)
  const tiles = (dip?.animals || []).slice(0, STRIP)
  const more = dip ? Math.max(0, (dip.animals_total || 0) - tiles.length) : 0
  const counts = { born: (data.births || []).length }
  for (const x of data.exits || []) counts[x.event_type] = (counts[x.event_type] || 0) + 1
  const movements = [
    counts.born ? `${fmtN(counts.born)} born` : null,
    ...Object.keys(EXIT_LABEL).map((k) => (counts[k] ? `${fmtN(counts[k])} ${EXIT_LABEL[k].toLowerCase()}` : null)),
  ].filter(Boolean)

  return (
    <div className={`card p-5 ${className}`} data-testid="kraal-feed-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="rounded-xl bg-primary-50 p-2 ring-1 ring-primary-100">
            <CalendarDays className="h-5 w-5 text-primary-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">At the kraal</h2>
        </div>
        <Link to="/cattle/feed" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline" data-testid="kraal-feed-link">
          This week&apos;s feed <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      {dip ? (
        <>
          <p className="mt-2 text-sm text-earth-600">
            <span className="font-medium text-gray-900">{formatDay(dip.day)}</span>
            {' · '}
            <span className="font-semibold text-gray-900" data-testid="kraal-feed-headline">{feedHeadline(dip)}</span>
            {dip.mine
              ? <>{dip.mine.animals ? ` (${pctText(dip.mine.seen, dip.mine.animals)})` : ''} · Ranch: {fmtN(dip.seen)} of {fmtN(dip.herd)}</>
              : <>{dip.herd ? ` (${pctText(dip.seen, dip.herd)})` : ''}</>}
          </p>
          {tiles.length > 0 && (
            <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1" data-testid="kraal-feed-strip">
              {tiles.map((a) => (
                <Link key={a.id} to={`/cattle/${a.id}`} title={a.name} className="shrink-0">
                  <MediaThumb src={a.still?.thumb || a.hero?.thumb || null} alt={a.name}
                              className="h-16 w-20 rounded-lg bg-earth-100 ring-1 ring-earth-200"
                              style={{ backgroundColor: a.hero?.dominant_rgb || undefined }}
                              glyph={<Beef className="h-6 w-6 text-earth-300" aria-hidden="true" />} />
                </Link>
              ))}
              {more > 0 && (
                <Link to="/cattle/feed" className="shrink-0 text-sm text-earth-600 hover:text-primary-700" data-testid="kraal-feed-more">
                  +{fmtN(more)} more
                </Link>
              )}
            </div>
          )}
        </>
      ) : (
        <p className="mt-2 text-sm text-earth-600">
          No dip day in the last 28 days. The last was {formatDay(data.last_dip_day)}.
        </p>
      )}
      {movements.length > 0 && (
        <p className="mt-3 text-sm text-earth-600" data-testid="kraal-feed-movements">
          {movements.join(' · ')} in the last 28 days
        </p>
      )}
    </div>
  )
}
